/**
 * Mock sports-data provider.
 *
 * Replays a hand-authored script through the deterministic game engine.
 * Every record it returns is flagged `isSimulated: true`, and the UI
 * badges it accordingly: a mock game is never presented as real
 * (Implementation Guide §21).
 *
 * Live progression is derived from elapsed wall-clock time since the game
 * was started, not from a timer or any stored counter. That means every
 * viewer in a Game Room sees the same play at the same moment with zero
 * writes and zero coordination.
 */

import { BUFFALO_AT_CAROLINA_SCRIPT } from "../../data/mockEvents/buffaloAtCarolina";
import { buildMockSchedule } from "../../data/mockGames/mockGames";
import { getTeam } from "../../data/teams";
import {
  GAME_STATUS,
  createInitialGameState,
  getEventsUpTo,
  getStateAtPlay,
} from "../../utils/gameEngine";

/** How long one simulated play takes in real time. */
export const MS_PER_PLAY = 6000;

/** Team ids used inside the authored script. */
const SCRIPT_AWAY = "BUF";
const SCRIPT_HOME = "CAR";

/**
 * Rebind the authored script onto an arbitrary pair of teams so every
 * mock fixture is playable, not just Buffalo at Carolina.
 */
function bindScript(script, { homeTeamId, awayTeamId }) {
  if (homeTeamId === SCRIPT_HOME && awayTeamId === SCRIPT_AWAY) return script;

  const remap = (teamId) => {
    if (teamId === SCRIPT_HOME) return homeTeamId;
    if (teamId === SCRIPT_AWAY) return awayTeamId;
    return teamId;
  };

  return script.map((play) => {
    const next = { ...play };
    if (play.teamId) next.teamId = remap(play.teamId);
    if (play.receivingTeamId) next.receivingTeamId = remap(play.receivingTeamId);
    return next;
  });
}

const scriptCache = new Map();

function scriptFor(game) {
  if (!game?.hasSimulation) return [];
  if (!scriptCache.has(game.gameId)) {
    scriptCache.set(game.gameId, bindScript(BUFFALO_AT_CAROLINA_SCRIPT, game));
  }
  return scriptCache.get(game.gameId);
}

/* ------------------------------------------------------------------ */
/* schedule                                                            */
/* ------------------------------------------------------------------ */

function schedule(now = Date.now()) {
  return buildMockSchedule(now).map((game) => ({
    ...game,
    totalPlays: game.hasSimulation ? BUFFALO_AT_CAROLINA_SCRIPT.length : 0,
  }));
}

async function getUpcomingGames({ limit = 10, now = Date.now() } = {}) {
  return schedule(now).slice(0, limit);
}

async function getGame(gameId, { now = Date.now() } = {}) {
  return schedule(now).find((game) => game.gameId === gameId) ?? null;
}

async function getTeamById(teamId) {
  return getTeam(teamId);
}

/**
 * Player data is not available in the MVP. We return null rather than
 * inventing a roster (PRD "never fabricate players").
 */
async function getPlayer() {
  return null;
}

/* ------------------------------------------------------------------ */
/* live state                                                          */
/* ------------------------------------------------------------------ */

/**
 * How many plays have happened.
 *
 * @param {{startedAt?: number|null, at?: number, playIndex?: number|null}} context
 * @param {number} totalPlays
 */
export function resolvePlayCount(context = {}, totalPlays = 0) {
  // An explicit index wins, used by manual step-through and by replays.
  if (Number.isFinite(context.playIndex)) {
    return Math.max(0, Math.min(totalPlays, Math.floor(context.playIndex)));
  }

  if (!context.startedAt) return 0;

  const elapsed = (context.at ?? Date.now()) - context.startedAt;
  if (elapsed < 0) return 0;

  return Math.min(totalPlays, Math.floor(elapsed / MS_PER_PLAY) + 1);
}

/**
 * Derived game state + the events that produced it.
 *
 * @param {string} gameId
 * @param {{startedAt?: number|null, at?: number, playIndex?: number|null}} context
 */
async function getLiveSnapshot(gameId, context = {}) {
  const at = context.at ?? Date.now();
  const game = await getGame(gameId, { now: at });

  if (!game) {
    return { game: null, state: null, events: [], playCount: 0, totalPlays: 0 };
  }

  const script = scriptFor(game);
  const totalPlays = script.length;
  const playCount = resolvePlayCount(context, totalPlays);

  const state =
    playCount === 0
      ? createInitialGameState(game)
      : getStateAtPlay(game, script, playCount);

  return {
    game,
    state: {
      ...state,
      isSimulated: true,
      provider: "mock",
      lastUpdatedAt: at,
      // A finished script means the game is over even if the clock reads > 0.
      status:
        playCount >= totalPlays && totalPlays > 0
          ? GAME_STATUS.FINAL
          : state.status,
    },
    events: playCount === 0 ? [] : getEventsUpTo(game, script, playCount),
    playCount,
    totalPlays,
  };
}

async function getLiveGame(gameId, context) {
  return (await getLiveSnapshot(gameId, context)).state;
}

async function getGameEvents(gameId, context) {
  return (await getLiveSnapshot(gameId, context)).events;
}

const mockProvider = Object.freeze({
  id: "mock",
  isSimulated: true,
  label: "Simulated game data",

  getUpcomingGames,
  getGame,
  getLiveGame,
  getGameEvents,
  getLiveSnapshot,
  getTeam: getTeamById,
  getPlayer,
});

export default mockProvider;
